/**
 * Cart drawer cross-sell (§5.6).
 *
 * The other category, for the cars in the basket, at exact prices — never a
 * "from" price. You know the car at this point, so a vague number reads as a
 * trap.
 *
 * 🔴 Rewritten 2026-08-16, from "one offer" to "every genuinely different thing
 * to buy". §5.6 said one card and that was wrong twice over, in ways a customer
 * meets on ordinary orders:
 *
 *   1. A car with both channels stocks an IN-channel set and an OUT-channel set.
 *      Those are two different purchases, and picking one for the customer is
 *      the guess this whole layer exists to avoid — the cart is the last place
 *      to start guessing. It offered whichever happened to be cheapest.
 *   2. A basket can hold parts for more than one car. Offering a single card
 *      silently picked one of them and left the other customer's second car
 *      unserved.
 *
 * So: for every car in the basket, the category that car is missing, every
 * distinct option, deduped by channel/body/pack exactly as the product page and
 * the landing pages dedupe (§3.2a). The cards are the SHARED component in
 * `airflux-xsell.js`, so the offer that named its channel on the product page
 * still names it here.
 *
 * It renders nothing at all when nothing is stocked, everything is out of stock,
 * or it is all already in the basket. §6 is explicit: no consolation offers.
 * Reach is naturally limited — the two ranges overlap on ~86 cars — so silence
 * is the common case by design, not a failure.
 */
(() => {
  'use strict';

  const CAR_KEY = 'airflux:car';
  const INDEX_KEY = 'airflux:index';

  /* Through `window.AirfluxStorage`, never `localStorage` directly: the car is
     kept for the visit unconditionally and mirrored across visits only on
     consent. See `snippets/airflux-storage.liquid`. */
  const readCar = () => window.AirfluxStorage?.readJSON(CAR_KEY) || null;

  /**
   * The index, sharing airflux-fitment.js's cache exactly.
   *
   * 🔴 The key is the ASSET URL, which carries Shopify's `?v=` cache-buster.
   * This file used to cache under a bare `airflux:index`, which meant two
   * things: the cross-sell could resolve against a pre-republish copy for a
   * whole session, and every visitor carried two 174 KB copies of the same
   * index because the widget's cleanup sweep matches `airflux:index:` with the
   * colon and never collected the other one.
   */
  async function getIndex() {
    const url = document.querySelector('[data-airflux-index-url]')?.dataset.airfluxIndexUrl;
    if (!url) return null;
    const cacheKey = `${INDEX_KEY}:${url}`;

    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        sessionStorage.removeItem(cacheKey);
      }
    }
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.json();
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      } catch {
        /* quota */
      }
      return data;
    } catch {
      return null;
    }
  }

  async function render() {
    /**
     * 🔴 Every mount, not the first one.
     *
     * The drawer is rendered into every page by the layout, so on `/cart` there
     * are TWO: the drawer's and the page's. `querySelector` filled the drawer's
     * — which is closed — and left the page's empty, so the basket page silently
     * never showed an offer.
     */
    const mounts = [...document.querySelectorAll('[data-airflux-crosssell]')];
    if (!mounts.length) return;
    // Flagged whether or not we end up showing anything — a considered decision
    // to stay silent must not look like "not evaluated yet" to the observer,
    // or it re-runs on every mutation forever.
    for (const m of mounts) {
      m.innerHTML = '';
      m.dataset.rendered = '1';
    }
    const mount = mounts[0];

    const index = await getIndex();
    if (!index) return;

    const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
    if (!cart || !cart.items.length) return;

    const inCart = new Set(cart.items.map((i) => i.handle));

    /**
     * 🔴 The offer follows the BASKET, not the stored car.
     *
     * This used to start from `airflux:car` and bail when there wasn't one,
     * which broke in both directions. A customer who reached a product page
     * from search or an ad and added to cart has no stored car, so the block
     * never appeared at all. Worse, a customer carrying an older car from an
     * earlier visit got an offer for THAT car: a basket holding a Citroën
     * Berlingo deflector was offered "Wiper Blades for your Škoda Octavia".
     *
     * What is in the basket is a fact; the stored car is a preference — it
     * decides which car's offers come FIRST, and settles one tie below.
     */
    const car = readCar();
    const own = car?.handle ? index.vehicles.find((v) => v.h === car.handle) ?? null : null;

    /**
     * 🔴 A part that fits rebadged twins counts for the customer's OWN twin
     * only, once they have told us which one they drive (2026-09-04).
     *
     * The MAN TGE and the VW Crafter are one van, so every TGE deflector is
     * linked to both rows — while the wipers are two listings, one per badge.
     * Read from the links alone, a basket holding that deflector belonged to
     * both vans and each was "missing" its own wiper: a customer who had just
     * resolved a TGE was offered blades for a Crafter underneath, which reads
     * as the site not knowing what they drive. Five such pairs in the catalogue
     * today (TGE/Crafter, Citigo/Up, 2008/e-2008, Partner/Rifter, A6/allroad).
     *
     * So an item the stored car fits is that car's and nobody else's. An item
     * the stored car does NOT fit still counts for every car it is linked to —
     * the search-and-ad path and the stale-car path above are untouched, and a
     * basket carrying a second car's part still gets that car's offer.
     */
    const ownItems = new Set(
      own ? [...(own.d ?? []), ...(own.w ?? [])].map((i) => index.products[i]?.h).filter((h) => inCart.has(h)) : []
    );
    const holds = (v, ids) =>
      (ids ?? []).some((i) => {
        const h = index.products[i]?.h;
        return inCart.has(h) && (v.h === own?.h || !ownItems.has(h));
      });

    const cartVehicles = index.vehicles.filter((v) => holds(v, v.d) || holds(v, v.w));
    if (!cartVehicles.length) return;

    if (own) {
      const at = cartVehicles.findIndex((v) => v.h === own.h);
      if (at > 0) cartVehicles.unshift(...cartVehicles.splice(at, 1));
    }

    /**
     * Per car, the category that car is MISSING from the basket.
     *
     * Deliberately per car rather than per basket. The old test asked whether
     * the basket held any deflector and any wiper at all, so a basket with a
     * deflector for the Kuga and a wiper for the Golf looked complete and was
     * offered nothing — when in fact neither car had its pair.
     *
     * A car's own membership is read from the fitment links, not from
     * `product_type`: the type string is merchandising copy and the links are
     * the fact.
     */
    const offers = [];
    const offered = new Set();
    for (const v of cartVehicles) {
      const has = { d: holds(v, v.d), w: holds(v, v.w) };
      // The stored car's answered version narrows ITS offer, the way the
      // landing page's does — a "4-door Saloon" A3 is not offered the
      // Convertible's blade (2026-09-04). Any other car in the basket has no
      // such answer and is offered everything it takes. Membership above is
      // still read off the whole row: which car an item belongs to is not a
      // question of version.
      const row = v.h === own?.h ? window.AirfluxXsell.forVersion(v, car?.version ?? null) : v;
      for (const want of ['d', 'w']) {
        if (has[want] || !has[want === 'd' ? 'w' : 'd']) continue;
        for (const c of window.AirfluxXsell.counterparts(index, row, want)) {
          // The same part can fit several rebadged twins, so it reaches this
          // loop once per car — first car named wins, which is why the stored
          // car was moved to the front above.
          if (!c.a || inCart.has(c.h) || offered.has(c.h)) continue;
          offered.add(c.h);
          offers.push({ c, v });
        }
      }
    }
    if (!offers.length) return;

    // The live check is the truth: the cached index can be a republish behind,
    // and an offer for something that has since sold out is worse than silence.
    const live = await Promise.all(
      offers.map((o) =>
        fetch(`/products/${o.c.h}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    const rows = offers
      .map((o, i) => ({ ...o, p: live[i] }))
      .filter((o) => o.p?.variants?.[0]?.available);
    if (!rows.length || !mount.isConnected) return;

    // Name the car only when more than one is in play. On a single-car basket
    // every card would carry the same line, which is furniture, not information.
    const manyCars = new Set(rows.map((r) => r.v.h)).size > 1;

    const html =
      `<div class="airflux-xsell af-cart-xsell">` +
      `<div class="af-cart-xsell__head">` +
      // The project's own phrase for this offer since §5.3 point 8, and short
      // enough to sit on one line beside a dismiss button in a 375px panel.
      `<h3 class="af-cart-xsell__title">Complete the set</h3>` +
      `<button type="button" class="af-cart-xsell__dismiss" data-airflux-crosssell-dismiss aria-label="Dismiss these suggestions">&times;</button>` +
      `</div>` +
      `<ul class="airflux-xsell__list af-cart-xsell__list" role="list">` +
      rows
        .map(({ c, p, v }) =>
          window.AirfluxXsell.card({
            c,
            p,
            car: manyCars ? `${index.makes[v.m]} ${v.n}` : null,
            // Channel and pack travel with the button so the added line carries
            // the same product facts the other add paths put on theirs; the
            // vehicle is per card now that one basket can offer several cars'.
            attrs:
              `data-airflux-crosssell-add data-variant="${p.variants[0].id}" data-vehicle="${escapeHtml(v.h)}"` +
              (c.c ? ` data-channel="${escapeHtml(c.c)}"` : '') +
              (c.k ? ` data-pack="${c.k}"` : ''),
          })
        )
        .join('') +
      `</ul>` +
      `</div>`;

    // The same offer on both surfaces. Ids would collide, so the card carries
    // none — everything the click handlers need is a `data-` attribute.
    for (const m of mounts) if (m.isConnected) m.innerHTML = html;

    document.dispatchEvent(
      new CustomEvent('airflux:upsell_shown', { detail: { handles: rows.map((r) => r.p.handle) } })
    );
  }

  async function add(button) {
    button.disabled = true;
    // The label lives in its own span on this card, as it does on the engine's:
    // writing to the button's `textContent` would delete the element every later
    // state has to write into.
    const label = button.querySelector('.af-btn__label') ?? button;
    label.textContent = 'Adding…';

    const index = await getIndex();
    // 🔴 Per BUTTON, not per mount. The drawer can now offer parts for more than
    // one car at once, so a single `data-vehicle` on the container would have
    // put the first car's name on the second car's order line — the exact
    // wrong-fitment failure §5.3.6 forbids, and invisible until the parcel
    // arrives.
    const vehicle = index?.vehicles.find((v) => v.h === button.dataset.vehicle);
    const car = readCar();
    // The same shared derivation the widget and the product form use, so all
    // three paths put identical facts on the order (§5.3.6). The offer is only
    // ever built from a product linked to this vehicle, so the car always fits.
    const properties = vehicle
      ? window.AirfluxOrder.carProperties({
          ...(car ?? {}),
          handle: vehicle.h,
          make: index.makes[vehicle.m],
          model: vehicle.n,
          years: `${vehicle.f}–${vehicle.t}`,
          body: car?.body || vehicle.b || null,
        }, { channel: button.dataset.channel, pack: button.dataset.pack })
      : {};

    try {
      const drawerEl = document.querySelector('cart-drawer');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variant), quantity: 1, properties }],
          // Ask Shopify to return the re-rendered drawer markup in the same
          // round trip, the way Dawn's own product form does.
          ...(drawerEl
            ? {
                sections: drawerEl.getSectionsToRender().map((s) => s.id),
                sections_url: window.location.pathname,
              }
            : {}),
        }),
      });
      if (!res.ok) {
        label.textContent = 'Unavailable';
        return;
      }
      document.dispatchEvent(new CustomEvent('airflux:upsell_accepted', { bubbles: true }));
      // Re-render through Dawn rather than reloading the page — reloading from
      // inside an open drawer loses the customer's place.
      const state = await res.json();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && state.sections) drawer.renderContents(state);
      else window.location.reload();
    } catch {
      label.textContent = 'Try again';
      button.disabled = false;
    }
  }

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /**
   * The empty basket (§6).
   *
   * It leads with the car rather than with the catalogue: "Continue shopping"
   * pointed at all 494 products, which is the one thing this store is built so
   * that nobody has to do. Two things are decided here rather than in Liquid,
   * because both live in browser storage and no server knows them:
   *
   *   - the stored car is named, so the basket confirms what the site thinks
   *     they drive before it asks them anything;
   *   - the button is aimed at that car's resolved home finder (`?car=`),
   *     which is the same address the search panel's car rows use.
   *
   * The button ships as a real link to the home page, so this is an upgrade and
   * never a dependency — with scripts blocked it still goes somewhere useful.
   *
   * `painted` is the same flag the cross-sell uses, for the same reason: writes
   * made in here re-enter the MutationObserver below, and fresh server markup
   * arrives without it, which is exactly the signal we want.
   */
  function paintEmpty(force) {
    const mount = document.querySelector('[data-airflux-cart-car]');
    if (!mount || (mount.dataset.painted && !force)) return;
    mount.dataset.painted = '1';

    const car = readCar();
    const label = car?.label ? [car.label, car.version].filter(Boolean).join(' · ') : null;
    if (!label) return; // stays hidden, and the button keeps its generic label

    mount.querySelector('[data-airflux-cart-car-name]').textContent = label;
    mount.hidden = false;

    const cta = document.querySelector('[data-airflux-cart-find]');
    if (!cta || !car.handle) return;
    const url = new URL(cta.getAttribute('href'), window.location.origin);
    url.searchParams.set('car', car.handle);
    cta.setAttribute('href', url.pathname + url.search);
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-airflux-crosssell-add]')) add(e.target.closest('[data-airflux-crosssell-add]'));
    if (e.target.closest('[data-airflux-crosssell-dismiss]')) {
      // Shown once, dismissible (§5.6) — and it stays dismissed for the session.
      // Every mount, because `/cart` carries the page's and the drawer's.
      sessionStorage.setItem('airflux:upsell-dismissed', '1');
      document.querySelectorAll('[data-airflux-crosssell]').forEach((m) => (m.innerHTML = ''));
    }

    // On a page that already carries a finder, use it instead of navigating —
    // the same call the header's car band makes, for the same reason: leaving
    // the page costs the customer their place in it.
    const find = e.target.closest('[data-airflux-cart-find]');
    if (!find) return;
    const widget = document.querySelector('airflux-fitment');
    if (!widget) return;

    e.preventDefault();
    document.querySelector('cart-drawer')?.close();
    widget.querySelector('[data-airflux-cascade]')?.removeAttribute('hidden');
    widget.querySelector('[data-airflux-submit]')?.removeAttribute('hidden');
    widget.scrollIntoView({
      block: 'center',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  });

  function maybeRender() {
    if (sessionStorage.getItem('airflux:upsell-dismissed')) return;
    render();
  }

  /**
   * Dawn replaces the drawer's innerHTML from the server on every cart change,
   * which wipes anything injected into it. Adding from a product page goes
   * through Dawn's own form, so no custom event fires either.
   *
   * Watching the drawer covers every path — product form, quantity change,
   * remove, and our own Add — without depending on Dawn's internals. The
   * `rendered` flag stops our own writes from retriggering the observer, and
   * it's absent on fresh server markup, which is exactly the signal we want.
   */
  function watchDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    new MutationObserver(() => {
      const mount = document.querySelector('[data-airflux-crosssell]');
      if (mount && !mount.dataset.rendered) maybeRender();
      // The empty block arrives the same way — swapped in by `updateQuantity`
      // when the last line is removed, with no event of its own to listen for.
      paintEmpty();
    }).observe(drawer, { childList: true, subtree: true });
  }

  /**
   * The basket PAGE needs its own watcher, and for the opposite reason to the
   * drawer's.
   *
   * The page's mount is deliberately outside `#main-cart-items`, so
   * `updateQuantity` — which replaces that element's `.js-contents` wholesale —
   * leaves it alone. That keeps it from being wiped, but it also means it never
   * looks stale: remove the deflector and the offer for its wiper would sit
   * there, still advertising a counterpart to something no longer in the basket.
   *
   * So this watches the LINES rather than the mount, and forces a repaint when
   * they change. Our own writes land outside the observed element, so there is
   * no loop to guard against.
   */
  function watchCartPage() {
    const items = document.getElementById('main-cart-items');
    if (!items) return;

    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      // One repaint per burst — `updateQuantity` replaces the fragment in one
      // go, but the browser may report it as several records.
      queueMicrotask(() => {
        pending = false;
        maybeRender();
        paintEmpty(true);

        /**
         * 🔴 And repaint the DRAWER, which is on this page too.
         *
         * Dawn deliberately ignores its own page-side updates:
         * `CartDrawerItems` subscribes to `cartUpdate` and returns early when
         * `source === 'cart-items'`, which `updateQuantity` always is. So the
         * drawer keeps whatever the server rendered at page load — change a
         * quantity on `/cart`, tap the basket icon, and it opens showing the
         * old lines and the old total.
         *
         * That was survivable while the drawer was a quiet mini-cart. It is not
         * now that both surfaces print a struck-through total in ink: two
         * different prices for the same basket, one of them wrong, is the kind
         * of thing a customer screenshots. Calling Dawn's own refresh is the
         * whole fix — no second implementation of the repaint.
         *
         * No loop: this observes `#main-cart-items`, and `onCartUpdate` only
         * replaces `cart-drawer-items` and `.cart-drawer__footer`.
         */
        document.querySelector('cart-drawer-items')?.onCartUpdate?.();
      });
    }).observe(items, { childList: true, subtree: true });
  }

  function init() {
    maybeRender();
    paintEmpty();
    watchDrawer();
    watchCartPage();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('airflux:car-changed', () => {
    maybeRender();
    paintEmpty(true);
  });
  document.addEventListener('airflux:added-to-cart', maybeRender);
})();
